import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for managing modal state and accessibility
 * @param {Object} options - Configuration options
 * @param {boolean} options.closeOnEscape - Whether to close modal on ESC key (default: true)
 * @param {boolean} options.closeOnOverlayClick - Whether to close modal on overlay click (default: true)
 * @param {boolean} options.lockBodyScroll - Whether to lock body scroll when modal is open (default: true)
 * @returns {Object} Modal state and control functions
 */
export const useModal = (options = {}) => {
  const {
    closeOnEscape = true,
    closeOnOverlayClick = true,
    lockBodyScroll = true
  } = options;

  const [isOpen, setIsOpen] = useState(false);
  const [currentLink, setCurrentLink] = useState(null);
  const previouslyFocusedElementRef = useRef(null);
  const modalRef = useRef(null);

  const openModal = (linkData) => {
    // Store the currently focused element to return focus later
    previouslyFocusedElementRef.current = document.activeElement;
    
    setCurrentLink(linkData);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setCurrentLink(null);
    
    // Return focus to the previously focused element
    if (previouslyFocusedElementRef.current) {
      previouslyFocusedElementRef.current.focus();
    }
  };

  // Handle ESC key press
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, closeOnEscape]);

  // Handle body scroll lock
  useEffect(() => {
    if (!lockBodyScroll) return;

    if (isOpen) {
      // Store original overflow style
      const originalOverflow = document.body.style.overflow;
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore original overflow on cleanup
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, lockBodyScroll]);

  // Handle focus trapping within modal
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const handleTabKey = (event) => {
      if (event.key !== 'Tab') return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        // Shift + Tab: moving backwards
        if (document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
      } else {
        // Tab: moving forwards
        if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  // Focus first focusable element when modal opens
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const firstFocusableElement = modalRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }
  }, [isOpen]);

  const handleOverlayClick = (event) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      closeModal();
    }
  };

  return {
    isOpen,
    currentLink,
    openModal,
    closeModal,
    modalRef,
    handleOverlayClick
  };
};